import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Edit } from "lucide-react";
import { fetchJSON } from "@/lib/api";
import { fetchEmployees } from "@/lib/employees";

// Types
interface LubricantProduct {
  id: string;
  lubricant_name: string;
  sale_rate: number;
  gst_percentage?: number;
  current_stock?: number;
}

interface Employee {
  id: string;
  employee_name: string;
}

export default function LubSale() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Global State
  const [saleDate, setSaleDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [shift, setShift] = useState<string>("S-1");

  // Form State Array
  interface SaleItem {
    id: string;
    selectedProduct: string;
    saleRate: string;
    quantity: string;
    discount: string;
    indentNo: string;
    amount: string;
    selectedEmployee: string;
    description: string;
    saleType: string;
    organization: string;
    vehicleNo: string;
    gstDetails: {
      tinGstNo: string;
      name: string;
      billNo: string;
    };
  }

  const [saleItems, setSaleItems] = useState<SaleItem[]>([{
    id: "1",
    selectedProduct: "",
    saleRate: "0",
    quantity: "",
    discount: "0",
    indentNo: "",
    amount: "0",
    selectedEmployee: "",
    description: "",
    saleType: "Cash",
    organization: "",
    vehicleNo: "",
    gstDetails: { tinGstNo: "", name: "", billNo: "" }
  }]);

  // GST Modal State
  const [activeGstId, setActiveGstId] = useState<string | null>(null);

  // Auto Fill State
  const [globalProduct, setGlobalProduct] = useState("");
  const [globalEmployee, setGlobalEmployee] = useState("");

  // Filter State
  const [searchFrom, setSearchFrom] = useState("");
  const [searchTo, setSearchTo] = useState("");
  const [searchProduct, setSearchProduct] = useState("All");
  const [filterText, setFilterText] = useState("");

  // Selection
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [editingSaleId, setEditingSaleId] = useState<string | null>(null);

  // Data Queries
  const { data: lubricants = [] } = useQuery<LubricantProduct[]>({
    queryKey: ["/api/lubricants"],
    queryFn: async () => {
      const res = await fetchJSON("/api/lubricants");
      if (!res.ok) return [];
      const json = await res.json();
      return json.rows || [];
    },
    staleTime: 0,
    refetchOnMount: true
  });

  const { data: employees = [] } = useQuery<Employee[]>({
    queryKey: ["/api/employees"],
    queryFn: fetchEmployees,
  });

  const { data: creditCustomers = [] } = useQuery({
    queryKey: ["/api/credit-customers"],
    queryFn: async () => {
      const res = await fetchJSON("/api/credit-customers");
      if (!res.ok) return [];
      const json = await res.json();
      return json.rows || [];
    }
  });

  const { data: sales = [], refetch } = useQuery({
    queryKey: ["/api/lubricant-sales", searchFrom, searchTo, searchProduct],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchFrom) params.append("from_date", searchFrom);
      if (searchTo) params.append("to_date", searchTo);
      if (searchProduct && searchProduct !== "All") params.append("product", searchProduct);

      const res = await fetchJSON(`/api/lubricant-sales?${params.toString()}`);
      const json = await res.json();
      return json.rows || [];
    }
  });

  // Derived State / Update Helper
  const updateItem = (id: string, field: keyof SaleItem, value: any) => {
    setSaleItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      const updated = { ...item, [field]: value };
      
      // Handle auto amount
      if (field === 'quantity' || field === 'saleRate' || field === 'discount') {
        const qty = parseFloat(updated.quantity) || 0;
        const rate = parseFloat(updated.saleRate) || 0;
        const disc = parseFloat(updated.discount) || 0;
        const total = (qty * rate) - disc;
        updated.amount = total > 0 ? total.toFixed(2) : "0";
      }
      return updated;
    }));
  };

  // Auto Fill Effects
  useEffect(() => {
    if (globalEmployee) {
      setSaleItems(prev => prev.map(item => ({ ...item, selectedEmployee: globalEmployee })));
    }
  }, [globalEmployee]);

  useEffect(() => {
    if (globalProduct) {
      const prod = lubricants.find(p => p.lubricant_name === globalProduct);
      setSaleItems(prev => prev.map(item => {
        if (!item.selectedProduct) {
          const qty = parseFloat(item.quantity) || 0;
          const rate = parseFloat(prod?.sale_rate?.toString() || "0");
          const disc = parseFloat(item.discount) || 0;
          const total = (qty * rate) - disc;
          return {
            ...item,
            selectedProduct: globalProduct,
            saleRate: prod?.sale_rate?.toString() || item.saleRate,
            amount: total > 0 ? total.toFixed(2) : "0"
          };
        }
        return item;
      }));
    }
  }, [globalProduct, lubricants]);

  const handleAddItem = () => {
    const prod = globalProduct ? lubricants.find(p => p.lubricant_name === globalProduct) : undefined;
    setSaleItems(prev => [...prev, {
      id: Date.now().toString(),
      selectedProduct: globalProduct || "",
      saleRate: prod?.sale_rate?.toString() || "0",
      quantity: "",
      discount: "0",
      indentNo: "",
      amount: "0",
      selectedEmployee: globalEmployee || "",
      description: "",
      saleType: "Cash",
      organization: "",
      vehicleNo: "",
      gstDetails: { tinGstNo: "", name: "", billNo: "" }
    }]);
  };

  const handleRemoveItem = (id: string) => {
    if (saleItems.length > 1) {
      setSaleItems(prev => prev.filter(item => item.id !== id));
    }
  };

  // Handlers
  const handleProductSelect = (id: string, val: string) => {
    updateItem(id, 'selectedProduct', val);
    const prod = lubricants.find(p => p.lubricant_name === val);
    if (prod) {
      updateItem(id, 'saleRate', prod.sale_rate?.toString() || "0");
    }
  };

  const resetForm = () => {
    setEditingSaleId(null);
    setSaleItems([{
      id: "1",
      selectedProduct: "",
      saleRate: "0",
      quantity: "",
      discount: "0",
      indentNo: "",
      amount: "0",
      selectedEmployee: "",
      description: "",
      saleType: "Cash",
      organization: "",
      vehicleNo: "",
      gstDetails: { tinGstNo: "", name: "", billNo: "" }
    }]);
  };

  const buildPayload = (item: SaleItem) => {
    const prod = lubricants.find(p => p.lubricant_name === item.selectedProduct);
    const gstVal = prod?.gst_percentage || 0;

    const selectedCust = creditCustomers.find((c: any) => c.id === item.organization);
    const custName = item.saleType === "Credit" 
      ? (selectedCust?.organization_name || "") 
      : (item.gstDetails.name || null);

    return {
      saleDate,
      shift,
      product: item.selectedProduct,
      saleRate: item.saleRate?.toString() || "0",
      quantity: item.quantity?.toString() || "0",
      discount: item.discount?.toString() || "0",
      indentNo: item.saleType === "Credit" ? (item.indentNo || "") : "",
      amount: item.amount?.toString() || "0",
      employeeId: item.selectedEmployee || globalEmployee,
      description: item.description || "",
      saleType: item.saleType,
      gst: gstVal?.toString() || "0",
      customerName: custName,
      creditCustomerId: item.saleType === "Credit" ? (item.organization || null) : null,
      vehicleNo: item.saleType === "Credit" ? (item.vehicleNo || "") : "",
      tinGstNo: item.saleType === "Credit" 
        ? (selectedCust?.tin_gst_no || null) 
        : (item.gstDetails.tinGstNo || null),
      billNo: item.gstDetails.billNo || null
    };
  };

  const readApiResult = async (res: Response) => {
    const text = await res.text();
    if (!text.trim()) {
      return { ok: res.ok, success: res.ok, error: res.ok ? "" : `Request failed (${res.status})` };
    }
    try {
      return JSON.parse(text);
    } catch {
      return { ok: false, success: false, error: text.slice(0, 200) || "Invalid server response" };
    }
  };

  const handleConfirm = async () => {
    // Basic validation
    if (saleItems.some(item => !item.selectedProduct || !item.quantity || (!item.selectedEmployee && !globalEmployee))) {
      toast({ title: "Validation Error", description: "Product, Quantity and Employee are required for all rows", variant: "destructive" });
      return;
    }

    try {
      if (editingSaleId) {
        const res = await fetchJSON(`/api/lubricant-sales/${editingSaleId}`, {
          method: "PUT",
          body: JSON.stringify(buildPayload(saleItems[0]))
        });
        const json = await readApiResult(res);
        if (!json.success && !json.ok) throw new Error(json.error || "Failed to update row");

        toast({ title: "Success", description: "Sale updated successfully" });
        refetch();
        resetForm();
        return;
      }

      for (const item of saleItems) {
        const res = await fetchJSON("/api/lubricant-sales", {
          method: "POST",
          body: JSON.stringify(buildPayload(item))
        });

        const json = await readApiResult(res);
        if (!json.success) {
          throw new Error(json.error || "Failed to save row");
        }
      }

      toast({ title: "Success", description: "Sales saved successfully" });
      refetch();
      resetForm();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleEdit = (row: any) => {
    setEditingSaleId(row.id);
    setSaleDate(row.sale_date ? new Date(row.sale_date).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10));
    setShift(row.shift || "S-1");
    setGlobalProduct("");
    setGlobalEmployee("");
    setSaleItems([{
      id: row.id || "1",
      selectedProduct: row.product || "",
      saleRate: String(row.sale_rate ?? "0"),
      quantity: String(row.quantity ?? ""),
      discount: String(row.discount ?? "0"),
      indentNo: String(row.indent_no ?? ""),
      amount: String(row.amount ?? "0"),
      selectedEmployee: row.employee_id || "",
      description: row.description || "",
      saleType: row.sale_type || "Cash",
      organization: row.credit_customer_id || "",
      vehicleNo: row.vehicle_no || "",
      gstDetails: {
        tinGstNo: row.tin_gst_no || "",
        name: row.customer_name || "",
        billNo: row.bill_no || "",
      }
    }]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this entry?")) return;
    try {
      const res = await fetchJSON(`/api/lubricant-sales/${id}`, { method: 'DELETE' });
      const json = await readApiResult(res);
      if (!json.success && !json.ok) throw new Error(json.error || "Failed to delete");
      refetch();
      toast({ title: "Deleted", description: "Entry deleted" });
      if (editingSaleId === id) resetForm();
    } catch (e: any) {
      toast({ variant: "destructive", title: "Error", description: e.message || "Failed to delete" });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!confirm(`Delete ${selectedIds.length} items?`)) return;

    try {
      for (const id of selectedIds) {
        const res = await fetchJSON(`/api/lubricant-sales/${id}`, { method: 'DELETE' });
        const json = await readApiResult(res);
        if (!json.success && !json.ok) throw new Error(json.error || `Failed to delete ${id}`);
      }
      setSelectedIds([]);
      refetch();
      toast({ title: "Batch Delete", description: "Items deleted" });
    } catch (e: any) {
      toast({ variant: "destructive", title: "Error", description: e.message || "Failed to delete selected items" });
    }
  };

  const filteredSales = sales.filter((s: any) => {
    if (!filterText) return true;
    const lower = filterText.toLowerCase();
    return s.product?.toLowerCase().includes(lower) ||
      s.employeeName?.toLowerCase().includes(lower) ||
      s.shift?.toLowerCase().includes(lower);
  });

  return (
    <div className="p-4 space-y-6 bg-white min-h-screen">
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
        <span className="font-semibold text-gray-700">Dashboard</span>
        <span>/</span>
        <span>Lubricants Sale</span>
      </div>

      {/* Main Form Card */}
      <div className="bg-blue-600 rounded-lg p-6 text-white shadow-lg space-y-6">
        <div className="text-2xl font-bold">Lubricant Sales</div>

        {/* Top Row: Date & Shift */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center bg-white rounded overflow-hidden h-10 w-auto">
            <div className="bg-blue-800 text-white px-4 h-full flex items-center font-bold text-sm">Choose Date</div>
            <Input
              type="date"
              value={saleDate}
              onChange={e => setSaleDate(e.target.value)}
              className="border-none text-black h-full rounded-none focus-visible:ring-0 w-40"
            />
            <div className="border-l border-gray-300 h-full flex items-center px-4 text-black bg-gray-50 text-sm font-medium w-32">
              {saleDate || "Sale Date"}
            </div>
          </div>

          <div className="flex bg-blue-700/50 rounded p-1 gap-4 px-4 border border-blue-400">
            {['S-1', 'S-2'].map(s => (
              <label key={s} className="flex items-center gap-2 cursor-pointer select-none">
                <div className={`w-4 h-4 rounded-full border-2 border-white flex items-center justify-center ${shift === s ? 'bg-white' : ''}`}>
                  {shift === s && <div className="w-2 h-2 rounded-full bg-blue-600" />}
                </div>
                <span className="font-bold">{s}</span>
                <input type="radio" className="hidden" checked={shift === s} onChange={() => setShift(s)} />
              </label>
            ))}
          </div>
        </div>

        {/* Auto Fill Box */}
        <div className="bg-blue-700/40 border border-blue-400/30 rounded-lg p-4 relative mt-4 md:w-1/2">
          <div className="absolute -top-3 left-4 text-xs font-bold text-blue-100 bg-blue-600 px-2">AUTO-FILL</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
            <Select value={globalProduct} onValueChange={setGlobalProduct}>
              <SelectTrigger className="bg-white text-gray-900 border-none h-10">
                <SelectValue placeholder="choose Item" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {lubricants.map((l) => (
                  <SelectItem key={l.id} value={l.lubricant_name}>{l.lubricant_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={globalEmployee} onValueChange={setGlobalEmployee}>
              <SelectTrigger className="bg-white text-gray-900 border-none h-10">
                <SelectValue placeholder="Choose Employee" />
              </SelectTrigger>
              <SelectContent>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.employee_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Dynamic Items Array */}
        <div className="space-y-4 border-t border-blue-400/30 pt-4">
          {saleItems.map((item, index) => (
            <div key={item.id} className="relative rounded-lg p-4 border border-blue-400/30 bg-blue-800/20">
              
              {/* Product Inputs Grid */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                <div className="md:col-span-3">
                  <span className="text-white text-xs mb-1 block">Lub Item</span>
                  <Select value={item.selectedProduct} onValueChange={(val) => handleProductSelect(item.id, val)}>
                    <SelectTrigger className="bg-white text-gray-900 border-none h-10 font-semibold">
                      <SelectValue placeholder="Select Product" />
                    </SelectTrigger>
                    <SelectContent>
                      {lubricants.map((l) => (
                        <SelectItem key={l.id} value={l.lubricant_name}>{l.lubricant_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <span className="text-white text-xs mb-1 block">Sale Rate</span>
                  <Input
                    placeholder="Sale Rate"
                    value={item.saleRate}
                    onChange={e => updateItem(item.id, 'saleRate', e.target.value)}
                    className="bg-white text-black h-10"
                  />
                </div>
                <div className="md:col-span-2">
                  <span className="text-white text-xs mb-1 block">Quantity</span>
                  <Input
                    placeholder="Quantity"
                    value={item.quantity}
                    onChange={e => updateItem(item.id, 'quantity', e.target.value)}
                    className="bg-white text-black h-10"
                  />
                  {item.selectedProduct && (
                    <span className="text-[11px] bg-yellow-400 text-black font-extrabold px-1.5 py-0.5 rounded block mt-1 w-max shadow-sm">
                      Available Stock : {lubricants.find(l => l.lubricant_name === item.selectedProduct)?.current_stock ?? 0}
                    </span>
                  )}
                </div>
                <div className="md:col-span-1">
                  <span className="text-white text-xs mb-1 block">Discount</span>
                  <Input
                    placeholder="Discount"
                    value={item.discount}
                    onChange={e => updateItem(item.id, 'discount', e.target.value)}
                    className="bg-white text-black h-10"
                  />
                </div>
                <div className="md:col-span-2">
                  <span className="text-white text-xs mb-1 block">Amount</span>
                  <Input
                    placeholder="Amount"
                    value={item.amount}
                    readOnly
                    className="bg-white text-black font-bold h-10 border-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <span className="text-white text-xs mb-1 block">Employee</span>
                  <Select value={item.selectedEmployee} onValueChange={(val) => updateItem(item.id, 'selectedEmployee', val)}>
                    <SelectTrigger className="bg-white text-gray-900 border-none h-10 font-semibold">
                      <SelectValue placeholder="Choose Employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((e) => (
                        <SelectItem key={e.id} value={e.id}>{e.employee_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Second Row of Inputs */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end mt-4">
                <div className="md:col-span-3">
                  <span className="text-white text-xs mb-1 block">Description</span>
                  <Input
                    placeholder="Description"
                    value={item.description}
                    onChange={e => updateItem(item.id, 'description', e.target.value)}
                    className="bg-white text-black h-10"
                  />
                </div>
                <div className={item.saleType === "Credit" ? "md:col-span-2 flex items-center justify-between gap-4 h-10 relative mt-5" : "md:col-span-3 flex items-center justify-between gap-4 h-10 relative mt-5"}>
                  <div className="flex flex-col gap-1 w-full justify-center">
                    <span className="text-white text-xs absolute -top-5">Sale Type</span>
                    <RadioGroup value={item.saleType} onValueChange={(val) => updateItem(item.id, 'saleType', val)} className="flex gap-4">
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="Cash" id={`r-cash-${item.id}`} className="border-white text-white" />
                        <Label htmlFor={`r-cash-${item.id}`} className="cursor-pointer font-bold">Cash</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="Credit" id={`r-credit-${item.id}`} className="border-white text-white" />
                        <Label htmlFor={`r-credit-${item.id}`} className="cursor-pointer font-bold">Credit</Label>
                      </div>
                    </RadioGroup>
                  </div>
                </div>

                {item.saleType === "Credit" ? (
                  <>
                    <div className="md:col-span-3">
                      <span className="text-white text-xs mb-1 block">Organization</span>
                      <Select value={item.organization} onValueChange={(val) => updateItem(item.id, 'organization', val)}>
                        <SelectTrigger className="bg-white text-gray-900 border-none h-10">
                          <SelectValue placeholder="choose Organization" />
                        </SelectTrigger>
                        <SelectContent>
                          {creditCustomers.map((c: any) => (
                            <SelectItem key={c.id} value={c.id}>{c.organization_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-1">
                      <span className="text-white text-xs mb-1 block">Vehicle No</span>
                      <Input
                        placeholder="0"
                        value={item.vehicleNo}
                        onChange={e => updateItem(item.id, 'vehicleNo', e.target.value)}
                        className="bg-white text-black h-10"
                      />
                    </div>
                    <div className="md:col-span-1">
                      <span className="text-white text-xs mb-1 block">Indent No</span>
                      <Input
                        placeholder="0"
                        value={item.indentNo}
                        onChange={e => updateItem(item.id, 'indentNo', e.target.value)}
                        className="bg-white text-black h-10"
                      />
                    </div>
                    <div className="md:col-span-2 flex items-center gap-2 h-10 relative mt-5">
                      <span className="text-white text-xs absolute -top-5">Fill GST</span>
                      <div className="flex items-center gap-2 w-full">
                        <Button size="icon" className="bg-[#8fb0e3] hover:bg-[#729cd6] p-0 w-10 h-10 rounded shrink-0 border-none" onClick={() => setActiveGstId(item.id)}>
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-black">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                          </svg>
                        </Button>
                        <Button size="icon" className="bg-transparent border border-white text-white hover:bg-white/20 w-8 h-8 rounded-full shrink-0 flex items-center justify-center p-0" onClick={handleAddItem}>
                          <span className="text-xl leading-none font-medium mt-[-2px]">+</span>
                        </Button>
                        {saleItems.length > 1 && (
                          <Button size="icon" className="bg-red-500 hover:bg-red-600 text-white w-8 h-8 rounded-full shrink-0 border-none flex items-center justify-center p-0" onClick={() => handleRemoveItem(item.id)}>
                            <span className="text-sm font-bold">×</span>
                          </Button>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="md:col-span-6 flex items-center gap-2 h-10 relative mt-5">
                    <span className="text-white text-xs absolute -top-5">Fill GST</span>
                    <div className="flex items-center gap-2 w-full">
                      <Button size="icon" className="bg-[#8fb0e3] hover:bg-[#729cd6] p-0 w-10 h-10 rounded shrink-0 border-none" onClick={() => setActiveGstId(item.id)}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-black">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                        </svg>
                      </Button>
                      <Button size="icon" className="bg-transparent border border-white text-white hover:bg-white/20 w-8 h-8 rounded-full shrink-0 flex items-center justify-center p-0" onClick={handleAddItem}>
                        <span className="text-xl leading-none font-medium mt-[-2px]">+</span>
                      </Button>
                      {saleItems.length > 1 && (
                        <Button size="icon" className="bg-red-500 hover:bg-red-600 text-white w-8 h-8 rounded-full shrink-0 border-none flex items-center justify-center p-0" onClick={() => handleRemoveItem(item.id)}>
                          <span className="text-sm font-bold">×</span>
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Confirm Button */}
        <div className="flex justify-center pt-4">
          <Button
            onClick={handleConfirm}
            className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-12 h-11 rounded-full text-md shadow-lg"
          >
            {editingSaleId ? "UPDATE" : "CONFIRM"}
          </Button>
          {editingSaleId && (
            <Button
              onClick={resetForm}
              variant="outline"
              className="ml-3 bg-white text-blue-700 hover:bg-blue-50 font-bold px-8 h-11 rounded-full"
            >
              CANCEL
            </Button>
          )}
        </div>
      </div>

      {/* Search Bar Section */}
      <Card className="shadow-sm border border-gray-100">
        <CardContent className="p-4 flex flex-wrap items-center gap-4 bg-white">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-bold text-gray-500">Search From</span>
            <Input type="date" value={searchFrom} onChange={e => setSearchFrom(e.target.value)} className="h-9 w-36 bg-gray-50" />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-bold text-gray-500">To Date</span>
            <Input type="date" value={searchTo} onChange={e => setSearchTo(e.target.value)} className="h-9 w-36 bg-gray-50" />
          </div>
          <div className="flex flex-col gap-1 flex-1">
            <span className="text-xs font-bold text-gray-500">Product</span>
            <Select value={searchProduct} onValueChange={setSearchProduct}>
              <SelectTrigger className="h-9 bg-gray-50">
                <SelectValue placeholder="Select Product" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Products</SelectItem>
                {lubricants.map((l) => (
                  <SelectItem key={l.id} value={l.lubricant_name}>{l.lubricant_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col justify-end">
            <Button onClick={() => refetch()} className="bg-orange-500 hover:bg-orange-600 h-9 px-8 font-bold text-white mt-5">
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table Controls */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div className="flex gap-2">
          <Button variant="destructive" className="h-9 bg-red-400 hover:bg-red-500 border-none text-white px-6" onClick={handleBulkDelete}>Delete</Button>
          <Button variant="outline" className="h-9 text-gray-600 border-gray-300 font-bold bg-white">Direct Print</Button>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex gap-2">
            <Button variant="outline" className="h-8 border-green-500 text-green-600 font-bold px-3">CSV</Button>
            <Button variant="outline" className="h-8 border-red-500 text-red-600 font-bold px-3">PDF</Button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-gray-600">Filter:</span>
            <Input
              placeholder="Type to filter..."
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
              className="h-9 w-48 bg-gray-50"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded overflow-hidden border">
        <Table>
          <TableHeader className="bg-white border-b">
            <TableRow className="h-10 hover:bg-transparent">
              <TableHead className="w-10 text-center"><Checkbox /></TableHead>
              <TableHead className="text-xs font-bold text-gray-600 text-center">S.No</TableHead>
              <TableHead className="text-xs font-bold text-gray-600">Txn No</TableHead>
              <TableHead className="text-xs font-bold text-gray-600">Sale Date</TableHead>
              <TableHead className="text-xs font-bold text-gray-600">Shift</TableHead>
              <TableHead className="text-xs font-bold text-gray-600">Product</TableHead>
              <TableHead className="text-xs font-bold text-gray-600">Sale Type</TableHead>
              <TableHead className="text-xs font-bold text-gray-600 text-center">Quantity</TableHead>
              <TableHead className="text-xs font-bold text-gray-600 text-right">Rate</TableHead>
              <TableHead className="text-xs font-bold text-gray-600 text-right">Amount</TableHead>
              <TableHead className="text-xs font-bold text-gray-600 text-center">Discount</TableHead>
              <TableHead className="text-xs font-bold text-gray-600 text-center">Indent No</TableHead>
              <TableHead className="text-xs font-bold text-gray-600">Employee</TableHead>
              <TableHead className="text-xs font-bold text-gray-600 text-center">Action</TableHead>
              <TableHead className="text-xs font-bold text-gray-600">Description</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredSales.length === 0 ? (
              <TableRow><TableCell colSpan={15} className="text-center py-10 text-gray-400">No lubricant sales data available. Add some sales to see them here.</TableCell></TableRow>
            ) : (
              filteredSales.map((row: any, i: number) => (
                <TableRow key={row.id} className="hover:bg-blue-50/30 border-b border-gray-100 h-10">
                  <TableCell className="text-center py-2">
                    <Checkbox
                      checked={selectedIds.includes(row.id)}
                      onCheckedChange={(checked) => {
                        if (checked) setSelectedIds([...selectedIds, row.id]);
                        else setSelectedIds(selectedIds.filter(id => id !== row.id));
                      }}
                    />
                  </TableCell>
                  <TableCell className="text-center text-xs py-2">{i + 1}</TableCell>
                  <TableCell className="text-xs font-mono py-2 text-gray-600">L{1000 + i}</TableCell>
                  <TableCell className="text-xs py-2 text-gray-700">
                    {(row.sale_date && !isNaN(new Date(row.sale_date).getTime()))
                      ? format(new Date(row.sale_date), 'dd-MMM-yyyy')
                      : (row.sale_date || "-")}
                  </TableCell>
                  <TableCell className="text-xs py-2 text-gray-700">{row.shift}</TableCell>
                  <TableCell className="text-xs font-medium py-2 text-gray-800">{row.product}</TableCell>
                  <TableCell className="text-xs py-2 text-gray-700">{row.sale_type}</TableCell>
                  <TableCell className="text-xs text-center py-2 text-gray-700">{row.quantity}</TableCell>
                  <TableCell className="text-xs text-right py-2 text-gray-700">{row.sale_rate}</TableCell>
                  <TableCell className="text-xs text-right font-bold py-2 text-gray-800">{row.amount}</TableCell>
                  <TableCell className="text-xs text-center py-2 text-gray-700">{row.discount}</TableCell>
                  <TableCell className="text-xs text-center py-2 text-gray-700">{row.indent_no || "0"}</TableCell>
                  <TableCell className="text-xs py-2 text-gray-700">{row.employeeName}</TableCell>
                  <TableCell className="text-center py-2">
                    <div className="flex justify-center gap-1">
                      <Button size="icon" variant="ghost" className="h-6 w-6 hover:bg-gray-100" onClick={() => handleEdit(row)}>
                        <Edit className="w-3 h-3 text-gray-600" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6 hover:bg-gray-100" onClick={() => handleDelete(row.id)}>
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs py-2 text-gray-500 truncate max-w-[150px]">{row.description}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination Footer */}
        <div className="flex justify-between items-center p-3 border-t">
          <span className="text-xs text-gray-500">Showing {filteredSales.length} of {filteredSales.length} entries</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="h-7 w-8 p-0" disabled>&lt;</Button>
            <Button variant="outline" size="sm" className="h-7 w-8 p-0 bg-gray-100 font-bold border-gray-300">1</Button>
            <Button variant="outline" size="sm" className="h-7 w-8 p-0" disabled>&gt;</Button>
          </div>
        </div>
      </div>

      {/* GST Modal */}
      <Dialog open={!!activeGstId} onOpenChange={(open) => !open && setActiveGstId(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Fill GST Details</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="gstNo" className="text-right font-bold">GST No</Label>
              <Input 
                id="gstNo" 
                value={activeGstId ? saleItems.find(i => i.id === activeGstId)?.gstDetails.tinGstNo || "" : ""} 
                onChange={e => activeGstId && updateItem(activeGstId, 'gstDetails', { ...saleItems.find(i => i.id === activeGstId)?.gstDetails, tinGstNo: e.target.value } as any)} 
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="cName" className="text-right font-bold">Name</Label>
              <Input 
                id="cName" 
                value={activeGstId ? saleItems.find(i => i.id === activeGstId)?.gstDetails.name || "" : ""} 
                onChange={e => activeGstId && updateItem(activeGstId, 'gstDetails', { ...saleItems.find(i => i.id === activeGstId)?.gstDetails, name: e.target.value } as any)} 
                className="col-span-3" 
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="bNo" className="text-right font-bold">Bill No</Label>
              <Input 
                id="bNo" 
                value={activeGstId ? saleItems.find(i => i.id === activeGstId)?.gstDetails.billNo || "" : ""} 
                onChange={e => activeGstId && updateItem(activeGstId, 'gstDetails', { ...saleItems.find(i => i.id === activeGstId)?.gstDetails, billNo: e.target.value } as any)} 
                className="col-span-3" 
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setActiveGstId(null)}>Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
